<template>
  <div class="app-container">
    <Toast />
    <ConfirmDialog />
    <LoadingOverlay 
      :visible="loading" 
      message="Carregando..."
    />
    <template v-if="!isAuthRoute && user">
      <div class="flex min-h-screen overflow-hidden">
        <Sidebar />
        <main class="flex-1 relative overflow-y-auto w-full">
          <!-- Header -->
          <div class="sticky top-0 z-10 bg-white/95 backdrop-blur-sm h-16 flex items-center justify-between border-b border-gray-100/75 px-4">
            <div class="flex items-center gap-4">
              <!-- Menu button is positioned by Sidebar component -->
              <div class="lg:hidden pl-12">
                <img :src="deepFinanceLogo" alt="Deep Finance Logo" class="h-8 w-auto" />
              </div>
            </div>
            <div class="flex items-center space-x-4">
              <OrganizationSwitcher />
            </div>
          </div>
          
          <!-- Content -->
          <div class="p-4">
            <router-view v-slot="{ Component }">
              <transition name="fade" mode="out-in">
                <div class="view-container">
                  <component :is="Component" />
                </div>
              </transition>
            </router-view>
          </div>
        </main>
      </div>
    </template>
    <template v-else>
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <div class="view-container">
            <component :is="Component" />
          </div>
        </transition>
      </router-view>
    </template>
  </div>
</template>

<script setup lang="ts">
import { RouterView } from 'vue-router'
import { useRoute, useRouter } from 'vue-router'
import Sidebar from './components/Sidebar.vue'
import { computed, onMounted, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { storeToRefs } from 'pinia'
import deepFinanceLogo from '@/assets/logos/logo_deep_finance.svg'
import OrganizationSwitcher from './components/organization/OrganizationSwitcher.vue'
import Toast from 'primevue/toast'
import ConfirmDialog from 'primevue/confirmdialog'
import LoadingOverlay from './components/LoadingOverlay.vue'

console.log('[App] Iniciando componente App...')

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

// Usar storeToRefs para manter a reatividade
const { loading, isAuthenticated, user, pendingVerification } = storeToRefs(authStore)

// Computed properties
const isAuthRoute = computed(() => ['login', 'register'].includes(route.name as string))

// Observar mudanças no estado de autenticação
watch(isAuthenticated, async (newValue) => {
  console.log('[App] Estado de autenticação alterado:', {
    isAuthenticated: newValue,
    currentRoute: route.name,
    isAuthRoute: isAuthRoute.value,
    pendingVerification: pendingVerification.value
  })

  // Se não estiver autenticado e não estiver em uma rota de autenticação
  if (!newValue && !isAuthRoute.value) {
    console.log('[App] Redirecionando para login...')
    router.push('/login')
  }
  // Se estiver autenticado e estiver em uma rota de autenticação
  else if (newValue && isAuthRoute.value) {
    // Não redirecionar se estiver pendente de verificação 2FA
    if (pendingVerification.value) {
      console.log('[App] Aguardando verificação 2FA...')
      return
    }
    console.log('[App] Usuário autenticado, redirecionando para dashboard...')
    router.push('/dashboard')
  }
})

onMounted(async () => {
  try {
    console.log('[App] Iniciando autenticação...')
    await authStore.init()
  } catch (error) {
    console.error('[App] Erro ao iniciar autenticação:', error)
  }
})
</script>

<style>
.app-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.view-container {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

:deep(.p-component-overlay) {
  z-index: 99 !important;
}
</style>